import { setShowOverlay, setMessages } from "../store/actions";
import store from "../store/store";
import * as wss from "./wss";
import Peer from "simple-peer";
import { fetchTURNCredentials, getTurnIceServers } from "./turn";

const defaultConstraints = {
  audio: true,
  video: {
    width: "480",
    height: "360",
  },
};

const onlyAudioConstraints = {
  audio: true,
  video: false,
};

let localStream;
let remoteStreams = [];
let peers = {};
let streams = [];
let peerCheckInterval;

export const getLocalPreviewAndInitRoomConnection = async (
  isRoomHost,
  identity,
  roomId = null,
  onlyAudio
) => {
  await fetchTURNCredentials();
  const constraints = onlyAudio ? onlyAudioConstraints : defaultConstraints;

  navigator.mediaDevices
    .getUserMedia(constraints)
    .then((stream) => {
      console.log("Successfully received local stream");
      localStream = stream;
      showLocalVideoPreview(localStream);

      store.dispatch(setShowOverlay(false));

      if (isRoomHost) {
        wss.createNewRoom(identity, onlyAudio, roomId);
      } else {
        wss.joinRoom(identity, roomId, onlyAudio);
      }

      // startInactivePeerCleanup(); // Start periodic cleanup
    })
    .catch((err) => {
      console.error("Error accessing local stream", err);
    });
};

const getConfiguration = () => {
  const turnIceServers = getTurnIceServers();

  return {
    iceServers: [
      {
        urls: "stun:stun.l.google.com:19302",
      },
      ...(turnIceServers || []),
    ],
  };
};

const messengerChannel = "messenger";

export const prepareNewPeerConnection = (connUserSocketId, isInitiator) => {
  const configuration = getConfiguration();

  peers[connUserSocketId] = new Peer({
    initiator: isInitiator,
    config: configuration,
    stream: localStream,
    channelName: messengerChannel,
  });

  peers[connUserSocketId].on("signal", (data) => {
    wss.signalPeerData({
      signal: data,
      connUserSocketId,
    });
  });

  peers[connUserSocketId].on("stream", (stream) => {
    addStream(stream, connUserSocketId);
    streams.push(stream);
    remoteStreams.push(stream);
  });

  peers[connUserSocketId].on("data", (data) => {
    appendNewMessage(JSON.parse(data));
  });

  peers[connUserSocketId].on("close", () => {
    removePeerConnection({ socketId: connUserSocketId });
  });

  peers[connUserSocketId].on("error", (err) => {
    removePeerConnection({ socketId: connUserSocketId });
  });
};

export const handleSignalingData = (data) => {
  if (peers[data.connUserSocketId]) {
    peers[data.connUserSocketId].signal(data.signal);
  }
};

export const removePeerConnection = (data) => {
  const socketId = data.socketId;

  const videoContainer = document.getElementById(socketId);
  const videoEl = document.getElementById(`${socketId}-video`);

  if (videoContainer && videoEl) {
    const tracks = videoEl.srcObject?.getTracks() || [];
    tracks.forEach((t) => t.stop());
    videoEl.srcObject = null;
    videoContainer.removeChild(videoEl);
    videoContainer.parentNode?.removeChild(videoContainer);
  }

  if (peers[socketId]) {
    try {
      peers[socketId].destroy();
      delete peers[socketId];
    } catch (err) {
      console.error(`Failed to destroy peer ${socketId}:`, err);
    }
  }

  remoteStreams = remoteStreams.filter(
    (stream) => !stream.getTracks().some((track) => track.id === socketId)
  );
};

export const cleanupAllConnections = () => {
  Object.keys(peers).forEach((socketId) => {
    removePeerConnection({ socketId });
  });

  if (localStream) {
    localStream.getTracks().forEach((track) => track.stop());
    localStream = null;
  }

  const videosContainer = document.getElementById("videos_portal");
  if (videosContainer) videosContainer.innerHTML = "";

  peers = {};
  streams = [];
  remoteStreams = [];

  store.dispatch(setMessages([]));
  store.dispatch(setShowOverlay(true));

  checkAndCleanupRoomId();

  //stopInactivePeerCleanup();
};

const checkAndCleanupRoomId = () => {
  const roomId = localStorage.getItem("roomId");
  const connectedPeers = Object.keys(peers || {});
  if (!roomId || connectedPeers.length === 0) {
    // console.log("No active connections. Removing roomId...");
    localStorage.removeItem("roomId");
    // console.log("After removal, roomId:", localStorage.getItem("roomId")); // Should be `null`
  } else {
    // console.log("Active connections found. RoomId is retained.");
  }
};


const showLocalVideoPreview = (stream) => {
  const videosContainer = document.getElementById("videos_portal");
  videosContainer.classList.add("videos_portal_styles");

  const videoContainer = document.createElement("div");
  videoContainer.classList.add("video_track_container");

  const videoElement = document.createElement("video");
  videoElement.autoplay = true;
  videoElement.muted = true;
  videoElement.playsInline = true;
  videoElement.srcObject = stream;

  videoElement.onloadedmetadata = () => {
    videoElement.play().catch((err) => console.warn("Playback failed:", err));
  };

  videoContainer.appendChild(videoElement);

  if (store.getState().connectOnlyWithAudio) {
    videoContainer.appendChild(getAudioOnlyLabel());
  }

  videosContainer.appendChild(videoContainer);
};

const addStream = (stream, connUserSocketId) => {
  const videosContainer = document.getElementById("videos_portal");

  const videoContainer = document.createElement("div");
  videoContainer.id = connUserSocketId;
  videoContainer.classList.add("remote_video_container");

  const videoElement = document.createElement("video");
  videoElement.autoplay = true;
  videoElement.muted = false;
  videoElement.playsInline = true;
  videoElement.srcObject = stream;
  videoElement.id = `${connUserSocketId}-video`;

  videoElement.onloadedmetadata = () => {
    videoElement.play().catch((err) =>
      console.warn("Remote video playback failed:", err)
    );
  };

  videoElement.addEventListener("click", () => {
    videoElement.classList.toggle("full_screen");
  });

  videoContainer.appendChild(videoElement);

  const participants = store.getState().participants;
  const participant = participants.find(
    (p) => p.socketId === connUserSocketId
  );

  if (participant?.onlyAudio) {
    videoContainer.appendChild(getAudioOnlyLabel(participant.identity));
  } else {
    videoContainer.style.position = "static";
  }

  videosContainer.appendChild(videoContainer);
};

const getAudioOnlyLabel = (identity = "") => {
  const labelContainer = document.createElement("div");
  labelContainer.classList.add("label_only_audio_container");

  const label = document.createElement("p");
  label.classList.add("label_only_audio_text");
  label.innerHTML = `Only audio ${identity}`;

  labelContainer.appendChild(label);
  return labelContainer;
};

export const toggleMic = (isMuted) => {
  localStream.getAudioTracks()[0].enabled = !isMuted;
};

export const toggleCamera = (isDisabled) => {
  localStream.getVideoTracks()[0].enabled = !isDisabled;
};

export const toggleScreenShare = (
  isScreenSharingActive,
  screenSharingStream = null
) => {
  switchVideoTracks(isScreenSharingActive ? localStream : screenSharingStream);
};

const switchVideoTracks = (stream) => {
  for (let socketId in peers) {
    const peer = peers[socketId];
    const oldTracks = peer.streams[0].getTracks();
    const newTracks = stream.getTracks();

    oldTracks.forEach((oldTrack) => {
      const replacementTrack = newTracks.find(
        (newTrack) => newTrack.kind === oldTrack.kind
      );
      if (replacementTrack) {
        peer.replaceTrack(oldTrack, replacementTrack, peer.streams[0]);
      }
    });
  }
};

const appendNewMessage = (messageData) => {
  const messages = store.getState().messages;
  store.dispatch(setMessages([...messages, messageData]));
};

export const sendMessageUsingDataChannel = (messageContent) => {
  const identity = store.getState().identity;

  const localMessageData = {
    content: messageContent,
    identity,
    messageCreatedByMe: true,
  };

  appendNewMessage(localMessageData);

  const messageData = {
    content: messageContent,
    identity,
  };

  const stringifiedMessageData = JSON.stringify(messageData);
  Object.values(peers).forEach((peer) => peer.send(stringifiedMessageData));
};

const startInactivePeerCleanup = () => {
  if (peerCheckInterval) clearInterval(peerCheckInterval);

  peerCheckInterval = setInterval(() => {
    Object.entries(peers).forEach(([socketId, peer]) => {
      if (peer.destroyed || peer._destroyed || peer._connected === false) {
        removePeerConnection({ socketId });
      }
    });
  }, 5000);
};

const stopInactivePeerCleanup = () => {
  if (peerCheckInterval) {
    clearInterval(peerCheckInterval);
    peerCheckInterval = null;
  }
};

export const getLocalStream = () => localStream;
export const getRemoteStreams = () => remoteStreams;

